import React, { useState, useEffect, useCallback } from 'react';
import { hasInstructorPermissions} from '../utils/permissions';
import DiscussionsApi from '../utils/api/DiscussionsApi';
import {
  MdSearch,
  MdPerson,
  MdFeedback,
  MdDateRange,
  MdInfo,
  MdFilterList,
  MdExpandMore,
  MdExpandLess,
} from 'react-icons/md';

// Constants moved outside component to avoid dependency issues
const EVALUATION_ATTRIBUTES = [
  'cardinalities', 'attributes', 'key_attribute', 'ternary', 'extended',
  'total_participation', 'entity_type', 'relationship_participants',
  'attribute_type', 'relationship_type', 'invalid_relationship'
];

const EVALUATION_OPTIONS = ['NA', 'FP', 'TP', 'TN', 'FN'];

const Discussions = ({ username, userRole, navigationData, classContext }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [allActiveUsers, setAllActiveUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState('');
  const [feedbackDetails, setFeedbackDetails] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [error, setError] = useState('');
  const [evaluationValues, setEvaluationValues] = useState({});
  const [pendingFeedbackSelection, setPendingFeedbackSelection] = useState(null);
  const [recentStudents, setRecentStudents] = useState([]);
  const [filterEnvironment, setFilterEnvironment] = useState('');
  const [filterRelationship, setFilterRelationship] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDiscussion, setShowDiscussion] = useState(true);
  const [showERD, setShowERD] = useState(false);
  const [showAIFeedback, setShowAIFeedback] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);

  // Enhanced ERD diagram renderer (same as notifications)
  const renderERDDiagram = (details) => {
    // Check if we have base64 SVG diagram data (regenerated from backend like Streamlit)
    if (details.erd_diagram) {
      try {
        // The backend regenerates SVG from ERD data
        const decodedSvg = atob(details.erd_diagram);
        
        if (decodedSvg.includes('<svg')) {
          // Display the SVG directly
          return (
            <div className="bg-white p-4 rounded border overflow-auto text-center">
              <div 
                dangerouslySetInnerHTML={{ __html: decodedSvg }}
                className="w-full flex justify-center"
              />
            </div>
          );
        } else {
          // Fallback: try as image if it's not SVG content
          return (
            <div className="bg-white p-4 rounded border overflow-auto text-center">
              <img 
                src={`data:image/svg+xml;base64,${details.erd_diagram}`}
                alt="ERD Diagram"
                className="max-w-full h-auto mx-auto border shadow-sm"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{display: 'none'}} className="text-red-600 text-sm">
                Failed to load diagram. Data length: {details.erd_diagram?.length || 0}
              </div>
            </div>
          );
        }
      } catch (error) {
        console.error('Error rendering ERD diagram:', error);
      }
    }
    
    // Fallback to plain text notation if available
    if (details.erd_plain) {
      return (
        <div className="bg-white p-4 rounded border">
          <h5 className="font-medium text-gray-700 text-sm mb-2">ERD Notation:</h5>
          <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
            {details.erd_plain}
          </pre>
        </div>
      );
    }

    // No diagram available
    return (
      <div className="bg-white p-4 rounded border text-center text-gray-500">
        <MdInfo className="text-2xl mx-auto mb-2 text-gray-400" />
        <p className="text-sm">No diagram available</p>
      </div>
    );
  };

  // Filter users based on search query
  const filteredUsers = allActiveUsers.filter(user => {
    // Only filter if query is at least 3 characters
    if (searchQuery.length < 3) {
      return false // or return true to show all users
    }

    const fullName = `${user.firstname} ${user.lastname}`.toLowerCase();
    const username = user.username.toLowerCase();
    const query = searchQuery.toLowerCase()
    
    return fullName.includes(query) || username.includes(query)
  })

  // Get total count before limiting
  const totalFilteredCount = filteredUsers.length;

  // Limit to first 15 results
  const limitedFilteredUsers = filteredUsers.slice(0, 15);

  // Fetch active users for instructors and TAs
  const fetchActiveUsers = useCallback(async () => {
    if (!hasInstructorPermissions(userRole)) return;

    try {
      const data = await DiscussionsApi.getActiveUsers(classContext);
      setAllActiveUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching active users:', error);
      setError('Failed to load active users. Please try again.');
    }
  }, [userRole, classContext]);

  // Filter feedbacks based on selected filters
  const filteredFeedbacks = feedbacks.filter(feedback => {
    // feedback structure: [id, username, environment, relationship, date, ...]

    // Filter by environment
    if (filterEnvironment && feedback[2] !== filterEnvironment) {
      return false;
    }

    // Filter by relationship
    if (filterRelationship && feedback[3] !== filterRelationship) {
      return false;
    }

    // Filter by date range
    if (filterDateFrom || filterDateTo) {
      const feedbackDate = new Date(feedback[4]);

      if(filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        if (feedbackDate < fromDate) return false;
      }

      if(filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999); // Set to end of day
        if (feedbackDate > toDate) return false;
      }
    }

    return true;
  });

  // Get unique values for filter dropdowns
  const uniqueEnvironments = [...new Set(feedbacks.map(f => f[2]))].sort();
  const uniqueRelationships = [...new Set(feedbacks.map(f => f[3]))].sort();

  // Reset UI when user changes
  const resetFeedbackUI = useCallback(() => {
    setFeedbacks([]); // Clear the feedbacks list
    setSelectedFeedback('');
    setFeedbackDetails(null);
    setDiscussions([]);
    setNewComment('');
    setEvaluationValues({});
    setError('');
    setPendingFeedbackSelection(null);
    // Clear filters too
    setFilterEnvironment('');
    setFilterRelationship('');
    setFilterDateFrom('');
    setFilterDateTo('');
  }, []);

  const clearFilters = useCallback(() => {
    setFilterEnvironment('');
    setFilterRelationship('');
    setFilterDateFrom('');
    setFilterDateTo('');
  }, []);

  // Fetch feedbacks based on user selection and role
  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      let targetUser;
      
      // Determine target user based on role
      if (hasInstructorPermissions(userRole)) {
        // Instructors and TAs must select a user
        targetUser = selectedUser;
        if (!targetUser) {
          setLoading(false);
          return;
        }
      } else {
        // Students see their own feedbacks
        targetUser = username;
      }

      const data = await DiscussionsApi.getUserFeedback(targetUser, classContext);
      const fetchedFeedbacks = data.feedbacks || [];
      setFeedbacks(fetchedFeedbacks);

      // Auto-select feedback if we have a pending selection
      if (pendingFeedbackSelection && fetchedFeedbacks.length > 0) {
        const targetFeedback = fetchedFeedbacks.find(feedback => feedback[0] === pendingFeedbackSelection);
        if (targetFeedback) {
          console.log('[Discussions] Auto-selecting pending feedback:', pendingFeedbackSelection);
          setSelectedFeedback(pendingFeedbackSelection);
          setPendingFeedbackSelection(null); // Clear the pending selection
        } else {
          console.log('[Discussions] Pending feedback not found in fetched feedbacks:', {
            pendingFeedbackId: pendingFeedbackSelection,
            availableFeedbacks: fetchedFeedbacks.map(f => f[0])
          });
        }
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setError('Failed to load feedbacks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [username, userRole, selectedUser, classContext, pendingFeedbackSelection]);

  // Fetch feedback details and discussions
  const fetchFeedbackDetails = useCallback(async (feedbackId) => {
    setLoading(true);
    setError('');

    try {
      // Fetch feedback details
      const detailsData = await DiscussionsApi.getFeedbackDetails(feedbackId, classContext);
      setFeedbackDetails(detailsData.feedback);

      // Fetch discussions
      const discussionsData = await DiscussionsApi.getDiscussions(feedbackId, classContext);
      setDiscussions(discussionsData.discussions || []);

      // Fetch evaluation values if instructor or TA
      if (hasInstructorPermissions(userRole)) {
        try {
          const evaluationData = await DiscussionsApi.getEvaluation(feedbackId, classContext);
          const values = {};
          EVALUATION_ATTRIBUTES.forEach((attr, index) => {
            values[attr] = evaluationData.values[index] || 'NA';
          });
          setEvaluationValues(values);
        } catch (evalError) {
          console.log('Evaluation endpoint not available yet:', evalError);
        }
      }
    } catch (error) {
      console.error('Error fetching feedback details:', error);
      setError('Failed to load feedback details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userRole, classContext]);

  // Submit new discussion comment
  const submitComment = async () => {
    if (!newComment.trim() || !selectedFeedback) return;

    setSubmittingComment(true);
    try {
      await DiscussionsApi.createDiscussion({
        feedbackId: selectedFeedback,
        username: username,
        text: newComment.trim()
      }, classContext);
      
      setNewComment('');
      fetchFeedbackDetails(selectedFeedback);
    } catch (error) {
      console.error('Error submitting comment:', error);
      setError('Failed to submit comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle evaluation changes
  const handleEvaluationChange = (attribute, value) => {
    setEvaluationValues(prev => ({
      ...prev,
      [attribute]: value
    }));
  };

  // Save evaluation
  const saveEvaluation = async () => {
    if (!selectedFeedback) return;

    try {
      const values = EVALUATION_ATTRIBUTES.map(attr => evaluationValues[attr] || 'NA');
      
      await DiscussionsApi.saveEvaluation({
        feedback_id: selectedFeedback,
        values: values
      }, classContext);
      
      alert('Evaluation saved successfully!');
    } catch (error) {
      console.error('Error saving evaluation:', error);
      alert('Error saving evaluation. Please try again.');
    }
  };

  // Load recently viewed students from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentStudents');
    if(stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setRecentStudents(parsed);
        }
      } catch (e) {
        console.error('Failed to load recent students:', e);
      }
    }
  }, []);

  // Save to localStorage whenever it changes (but not on initial empty state)
  useEffect(() => {
    if (recentStudents.length > 0) {
      localStorage.setItem('recentStudents', JSON.stringify(recentStudents));
    }
  }, [recentStudents]);

  // Handle navigation data from notifications - improved approach
  useEffect(() => {
    console.log('[Discussions] Navigation data useEffect triggered', {
      navigationData: navigationData,
      feedbackId: navigationData?.feedbackId,
      autoExpand: navigationData?.autoExpand,
      userRole: userRole,
      allActiveUsersLength: allActiveUsers.length,
      selectedUser: selectedUser
    });
    
    if (navigationData?.feedbackId && navigationData?.autoExpand) {
      console.log('[Discussions] Processing navigation data with auto-expand');
      
      // Store the feedback ID we want to select
      setPendingFeedbackSelection(navigationData.feedbackId);
      
      // For instructors/TAs, set the user first if needed
      if (hasInstructorPermissions(userRole) && navigationData.username) {
        // Wait for users to be loaded
        if (allActiveUsers.length === 0) {
          console.log('[Discussions] Users not loaded yet, waiting...');
          return;
        }
        
        // Check if the target user exists
        const targetUser = allActiveUsers.find(user => user.username === navigationData.username);
        console.log('[Discussions] Looking for target user:', {
          targetUsername: navigationData.username,
          targetUserFound: !!targetUser,
          currentSelectedUser: selectedUser
        });
        
        if (targetUser && selectedUser !== navigationData.username) {
          console.log('[Discussions] Setting selected user:', navigationData.username);
          setSelectedUser(navigationData.username);
          // The pending feedback will be selected after feedbacks are loaded
          return;
        }
      }
      
      // For students or when instructor already has correct user selected,
      // try to select the feedback directly
      console.log('[Discussions] Attempting to select feedback directly:', navigationData.feedbackId);
      setSelectedFeedback(navigationData.feedbackId);
    } else {
      console.log('[Discussions] Navigation data conditions not met', {
        hasFeedbackId: !!navigationData?.feedbackId,
        hasAutoExpand: !!navigationData?.autoExpand
      });
    }
  }, [navigationData, userRole, allActiveUsers, selectedUser]);

  // Initialize component based on user role
  useEffect(() => {
    if (hasInstructorPermissions(userRole)) {
      // Instructors and TAs need to fetch active users first
      fetchActiveUsers();
    } else {
      // Students fetch their own feedbacks immediately
      fetchFeedbacks();
    }
  }, [userRole, fetchActiveUsers, fetchFeedbacks]);

  // Fetch feedbacks when instructor or TA selects a user
  useEffect(() => {
    if (selectedUser && hasInstructorPermissions(userRole)) {
      fetchFeedbacks();
    }
  }, [selectedUser, userRole, fetchFeedbacks]);

  // Fetch details when feedback is selected
  useEffect(() => {
    if (selectedFeedback) {
      fetchFeedbackDetails(selectedFeedback);
    }
  }, [selectedFeedback, fetchFeedbackDetails]);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // FeedbackCard component similar to FeedbackLog.jsx
  const FeedbackCard = ({ feedback, isSelected, onClick }) => {
    const [id, , environment, relationship, date] = feedback;

    return (
      <div
        onClick={() => onClick(id)}
        className={`p-4 rounded-lg shadow-md border cursor-pointer transition-all duration-200 hover:shadow-lg ${
          isSelected
            ? 'border-blue-500 bg-blue-50 shadow-lg'
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-gray-800">{environment}</h3>
          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
            {formatTimestamp(date)}
          </span>
        </div>

        <div className="space-y-1">
          <div className="flex items-center text-sm">
            <span className="font-medium text-gray-600 mr-2">Relationship:</span>
            <span className="text-gray-800 truncate">{relationship}</span>
          </div>
        </div>

        {isSelected && (
          <div className="mt-3 pt-3 border-t border-blue-200">
            <div className="flex items-center text-sm text-blue-600">
              <span>Selected</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-md m-6 p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800 mb-1">
              Feedback Discussions
            </h1>
            <p className="text-gray-600 text-sm">
              View feedback details and participate in discussions about ERD submissions
            </p>
          </div>
          <div className="hidden md:block">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <MdFeedback className="text-white text-lg" />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <span className="text-red-500 text-xl mr-3">⚠️</span>
              <div>
                <p className="text-red-800 font-medium">Error</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Card */}
      <div className="flex-1 flex bg-white rounded-lg shadow-md mx-6 mb-6 overflow-hidden min-h-0">
        {/* Left Sidebar - Student Selection, Filters & Feedback List */}
        <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-gray-50 rounded-l-lg flex flex-col min-h-0 overflow-hidden">
          {/* Student Selection Section */}
          {hasInstructorPermissions(userRole) && (
            <div className="p-4 border-b border-gray-200 bg-white rounded-tl-lg flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <MdPerson className="text-blue-600" />
                Select Student
              </h2>

              <div className="space-y-3">
                {/* Search Bar with Dropdown */}
                <div className="relative">
                  <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowSearchDropdown(true)}
                    className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />

                  {/* Search Results Dropdown */}
                  {showSearchDropdown && searchQuery && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-96 overflow-y-auto z-20">
                      {searchQuery.length < 3 ? (
                        /* Show hint when less than 3 characters */
                        <div className="px-4 py-3 text-gray-500 text-sm flex items-center gap-2">
                          <MdInfo className="text-blue-500" />
                          <span>Type at least 3 characters to search...</span>
                        </div>
                      ) : limitedFilteredUsers.length > 0 ? (
                        /* Show search results */
                        <>
                          {limitedFilteredUsers.map((user) => (
                            <button
                              key={user.username}
                              onClick={() => {
                                setSelectedUser(user.username);
                                setSearchQuery('');
                                setShowSearchDropdown(false);
                                resetFeedbackUI(); // Reset UI when user changes

                                // Add to recent students
                                setRecentStudents(prev => {
                                  // Remove if already exists (to move to front)
                                  const filtered = prev.filter(u => u.username !== user.username);
                                  // Add to front, limit to 5
                                  return [user, ...filtered].slice(0, 10);
                                });
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                            >
                              <div className="font-medium text-gray-800">
                                {user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : user.username}
                              </div>
                              <div className="text-sm text-gray-600">
                                {user.username}
                              </div>
                            </button>
                          ))}
                          {/* Show "more results" indicator if needed */}
                          {totalFilteredCount > 15 && (
                            <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50 text-center border-t border-gray-200">
                              Showing 15 of {totalFilteredCount} results. Refine your search for more specific results.
                            </div>
                          )}
                        </>
                      ) : (
                        /* Show no results message */
                        <div className="px-4 py-3 text-gray-500 text-sm text-center">
                          <MdSearch className="text-2xl mx-auto mb-2 text-gray-400" />
                          <p>No students found matching "{searchQuery}"</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Recently Viewed Students */}
                {!selectedUser && recentStudents.length > 0 && (
                  <div className="mb-2">
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <MdPerson className="text-blue-600" />
                      Recently Viewed:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {recentStudents.slice(0, 6).map((user) => (
                        <button
                          key={user.username}
                          onClick={() => {
                            setSelectedUser(user.username);
                            resetFeedbackUI();
                          }}
                          className="group px-3 py-1.5 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-blue-50 hover:to-blue-100 border border-gray-200 hover:border-blue-300 rounded-lg text-xs transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <div className="flex items-center gap-2">
                            <MdPerson className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                            <div className="text-left">
                              <div className="font-medium text-gray-800 group-hover:text-blue-700 transition-colors">
                                {user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : user.username}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedUser && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Selected:</span> {
                        (() => {
                          const user = allActiveUsers.find(u => u.username === selectedUser);
                          return user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname} (${selectedUser})` : selectedUser;
                        })()
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Click outside to close search dropdown */}
              {showSearchDropdown && (
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowSearchDropdown(false)}
                />
              )}
            </div>
          )}

          {/* Feedback Filters Section (collapsible with animation) */}
          {selectedUser && feedbacks.length > 0 && (
            <div className="border-b border-gray-200 bg-white flex-shrink-0">
              {/* Filter Header with Toggle */}
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setShowFilters(!showFilters)}>
                <h2 className="text-md font-semibold text-gray-800 flex items-center gap-2">
                  <MdFilterList className="text-blue-600 text-xl" />
                  Filters
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    ({filteredFeedbacks.length} of {feedbacks.length})
                  </span>
                </h2>
                {showFilters ? (
                  <MdExpandLess className="text-xl text-gray-600" />
                ) : (
                  <MdExpandMore className="text-xl text-gray-600" />
                )}
              </div>

              {/* Collapsible Filter Section with smooth animation */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${showFilters ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-4 pb-4 space-y-3">
                  {/* Environment Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Environment
                    </label>
                    <select
                      value={filterEnvironment}
                      onChange={(e) => setFilterEnvironment(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">All Environments</option>
                      {uniqueEnvironments.map((env) => (
                        <option key={env} value={env}>
                          {env}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Relationship Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Relationship
                    </label>
                    <select
                      value={filterRelationship}
                      onChange={(e) => setFilterRelationship(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">All Relationships</option>
                      {uniqueRelationships.map((rel) => (
                        <option key={rel} value={rel}>
                          {rel}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Range Filters */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <MdDateRange className="text-blue-600" />
                      From Date
                    </label>
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <MdDateRange className="text-blue-600" />
                      To Date
                    </label>
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Clear Filters Button */}
                  <button
                    onClick={clearFilters}
                    disabled={!filterEnvironment && !filterRelationship && !filterDateFrom && !filterDateTo}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Clear All Filters
                  </button>

                  {/* Active Filters Summary */}
                  {(filterEnvironment || filterRelationship || filterDateFrom || filterDateTo) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                      <p className="text-xs font-medium text-blue-800 mb-1">Active:</p>
                      <div className="flex flex-wrap gap-1">
                        {filterEnvironment && (
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            Env: {filterEnvironment}
                          </span>
                        )}
                        {filterRelationship && (
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            Rel: {filterRelationship}
                          </span>
                        )}
                        {filterDateFrom && (
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            From: {filterDateFrom}
                          </span>
                        )}
                        {filterDateTo && (
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            To: {filterDateTo}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Feedback List as Cards */}
          <div className="flex-1 min-h-0">
            {!selectedUser && hasInstructorPermissions(userRole) ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                <MdPerson className="text-6xl mb-4 text-gray-400" />
                <p className="text-lg mb-2 text-center">Select a student</p>
                <p className="text-sm text-center">
                  Choose a student to view their feedback entries
                </p>
              </div>
            ) : loading && !selectedFeedback ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
                <span className="text-gray-600">Loading feedbacks...</span>
              </div>
            ) : feedbacks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                <MdFeedback className="text-6xl mb-4 text-gray-400" />
                <p className="text-lg mb-2 text-center">No feedback found</p>
                <p className="text-sm text-center">
                  This student hasn't submitted any feedback yet.
                </p>
              </div>
            ) : filteredFeedbacks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                <MdFilterList className="text-6xl mb-4 text-gray-400" />
                <p className="text-lg mb-2 text-center">No matches</p>
                <p className="text-sm text-center">
                  No feedback matches the current filters.
                </p>
              </div>
            ) : (
              <div className="h-full overflow-y-auto p-4 space-y-3">
                {filteredFeedbacks.map((feedback) => (
                  <FeedbackCard
                    key={feedback[0]}
                    feedback={feedback}
                    isSelected={selectedFeedback === feedback[0]}
                    onClick={(id) => setSelectedFeedback(id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Content Area - Feedback Details */}
        <div className="flex-1 bg-white rounded-r-lg flex flex-col min-h-0 overflow-hidden">
          {!selectedFeedback ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <MdFeedback className="text-6xl mb-4 text-gray-400" />
              <p className="text-xl mb-2">Select a feedback entry</p>
              <p className="text-gray-400">Choose a feedback card from the left to view details</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading feedback details...</span>
            </div>
          ) : feedbackDetails ? (
            <div className="flex-1 min-h-0">
              <div className="h-full overflow-y-auto p-6 space-y-4">
                {/* Feedback Header */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Feedback Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <MdPerson className="text-blue-600" />
                      <div>
                        <h4 className="font-medium text-gray-700 text-sm">Student:</h4>
                        <p className="text-gray-900">{feedbackDetails.username}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MdInfo className="text-green-600" />
                      <div>
                        <h4 className="font-medium text-gray-700 text-sm">Environment:</h4>
                        <p className="text-gray-900">{feedbackDetails.environment}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MdFeedback className="text-purple-600" />
                      <div>
                        <h4 className="font-medium text-gray-700 text-sm">Relationship:</h4>
                        <p className="text-gray-900">{feedbackDetails.relationship}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MdDateRange className="text-orange-600" />
                      <div>
                        <h4 className="font-medium text-gray-700 text-sm">Date:</h4>
                        <p className="text-gray-900">{formatTimestamp(feedbackDetails.date)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Discussions Section */}
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <button
                    onClick={() => setShowDiscussion(!showDiscussion)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-medium text-gray-700 text-lg flex items-center gap-2">
                      <MdFeedback className="text-blue-600" />
                      Discussion
                      {discussions.length > 0 && (
                        <span className="text-sm font-normal text-gray-500">
                          ({discussions.length})
                        </span>
                      )}
                    </h4>
                    {showDiscussion ? (
                      <MdExpandLess className="text-xl text-gray-600" />
                    ) : (
                      <MdExpandMore className="text-xl text-gray-600" />
                    )}
                  </button>

                  <div className={`transition-all duration-300 ease-in-out ${showDiscussion ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                    <div className="p-4 border-t">
                      {/* Existing discussions */}
                      <div className="space-y-3 mb-6">
                        {discussions.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <MdFeedback className="text-4xl mx-auto mb-2 text-gray-400" />
                            <p className="text-sm">No discussions yet. Start the conversation!</p>
                          </div>
                        ) : (
                          discussions.map((discussion, index) => (
                            <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-gray-800">
                                  {discussion.username}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatTimestamp(discussion.timestamp)}
                                </span>
                              </div>
                              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {discussion.text}
                              </p>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add new comment */}
                      <div className="border-t pt-4">
                        <h5 className="font-medium text-gray-700 mb-2">Add to Discussion</h5>
                        <div className="space-y-3">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Share your thoughts, ask questions, or provide additional feedback..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows="4"
                          />
                          <div className="flex justify-end">
                            <button
                              onClick={submitComment}
                              disabled={!newComment.trim() || submittingComment}
                              className="bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                              {submittingComment ? 'Posting...' : 'Post Comment'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ERD Diagram */}
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <button
                    onClick={() => setShowERD(!showERD)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-medium text-gray-700 text-lg flex items-center gap-2">
                      <MdInfo className="text-green-600" />
                      ERD Diagram
                    </h4>
                    {showERD ? (
                      <MdExpandLess className="text-xl text-gray-600" />
                    ) : (
                      <MdExpandMore className="text-xl text-gray-600" />
                    )}
                  </button>

                  <div className={`transition-all duration-300 ease-in-out ${showERD ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                    <div className="p-4 border-t">
                      {renderERDDiagram(feedbackDetails)}
                    </div>
                  </div>
                </div>

                {/* AI Feedback Content */}
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <button
                    onClick={() => setShowAIFeedback(!showAIFeedback)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-medium text-gray-700 text-lg flex items-center gap-2">
                      <MdFeedback className="text-purple-600" />
                      AI Feedback
                    </h4>
                    {showAIFeedback ? (
                      <MdExpandLess className="text-xl text-gray-600" />
                    ) : (
                      <MdExpandMore className="text-xl text-gray-600" />
                    )}
                  </button>

                  <div className={`transition-all duration-300 ease-in-out ${showAIFeedback ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                    <div className="p-4 border-t">
                      <div className="bg-blue-50 rounded p-4">
                        <div className="space-y-2">
                          {feedbackDetails.feedback && feedbackDetails.feedback.split('\n').map((line, index) => (
                            <p key={index} className="text-gray-800 leading-relaxed">
                              {line.replace(/'/g, '`')}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Evaluation Section (Instructor/TA only) */}
                {hasInstructorPermissions(userRole) && (
                  <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => setShowEvaluation(!showEvaluation)}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h4 className="font-medium text-gray-700 text-lg flex items-center gap-2">
                        <MdInfo className="text-orange-600" />
                        Instructor Evaluation
                      </h4>
                      {showEvaluation ? (
                        <MdExpandLess className="text-xl text-gray-600" />
                      ) : (
                        <MdExpandMore className="text-xl text-gray-600" />
                      )}
                    </button>

                    <div className={`transition-all duration-300 ease-in-out ${showEvaluation ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                      <div className="p-4 border-t">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {EVALUATION_ATTRIBUTES.map((attribute) => (
                            <div key={attribute} className="space-y-1">
                              <label className="block text-sm font-medium text-gray-600 capitalize">
                                {attribute.replace(/_/g, ' ')}
                              </label>
                              <select
                                value={evaluationValues[attribute] || 'NA'}
                                onChange={(e) => handleEvaluationChange(attribute, e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                {EVALUATION_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={saveEvaluation}
                          className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                          Save Evaluation
                        </button>

                        {/* Evaluation Legend */}
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-2">Evaluation Guide:</h5>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><strong>TN:</strong> No mistakes, AI confirms the correctness</p>
                            <p><strong>TP:</strong> Mistake, AI correctly detect mistake</p>
                            <p><strong>FP:</strong> No mistake, AI incorrectly detect mistake</p>
                            <p><strong>FN:</strong> Mistake, AI incorrectly confirms the correctness</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <MdInfo className="text-6xl mb-4 text-gray-400" />
              <p className="text-center py-8">
                Failed to load feedback details.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Discussions;